/**
 * ReplicationSerializer - serializes and deserializes actor replicated properties.
 *
 * ## Wire format
 *
 * Each actor replication payload (`Uint8Array`) is laid out as:
 * ```
 * [actor bitmask: ceil(N/8) bytes]
 * [values for each set bit in property-index order]
 * [uint8 componentCount]
 *   [uint8 subId] [component bitmask: ceil(M/8) bytes] [component values...]
 *   ...
 * ```
 *
 * Value encoding per `ReplicationNetType` (all multi-byte values are little-endian):
 * - `bool`             : 1 byte (0 or 1)
 * - `uint8/int8`       : 1 byte
 * - `uint16/int16`     : 2 bytes
 * - `int32/uint32`     : 4 bytes
 * - `float32`          : 4 bytes
 * - `float64`          : 8 bytes
 * - `string`           : uint16 byte length + UTF-8 bytes
 * - `actorRef`         : uint8 byte length (0, 8, or 16) + raw hex bytes
 * - `vector2`          : 2× float32 (x, y)
 * - `vector3/euler`    : 3× float32 (x, y, z)
 * - `vector4/quaternion`: 4× float32 (x, y, z, w)
 * - `array`            : uint16 count + N × element value
 * - `struct`           : [bitmask][values] recursively
 * - `class`            : if polymorphic: [uint32 hash][bitmask][values], else [bitmask][values]
 *
 * Nullable properties are prefixed with a 1-byte presence flag (0 = null, 1 = value follows).
 *
 * Per-property and per-object encoding details live in {@link ObjectSerializer}.
 */
import 'reflect-metadata';

import {
  decodeObjectFromBinary,
  serializeObjectAndEncode,
} from './ObjectSerializer.js';
import {
  BinaryReader,
  BinaryWriter,
} from './ReplicationBinaryCodec.js';

import type { Actor } from '../actors/Actor.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The key used in the `state` record to store per-component JS-value snapshots.
 * Used internally for delta comparison; NOT sent on the wire.
 */
export const REPLICATION_COMPONENTS_KEY = '$components' as const;

/**
 * Result of {@link ReplicationSerializer.serializeActor}.
 */
export interface ActorReplicationSnapshot {
  /**
   * Complete JS-value snapshot of all replicated properties (actor + components).
   * Pass this as `lastState` on the next tick for delta comparison.
   * Keyed by property name; components are under `$components.{uuid}`.
   */
  state: Record<string, unknown>;

  /**
   * Binary-encoded wire payload ready to send.
   * Contains either the full state (when `lastState` was null) or a delta.
   * `null` when nothing changed — do not send an update packet.
   */
  payload: Uint8Array | null;
}

// ---------------------------------------------------------------------------
// ReplicationSerializer
// ---------------------------------------------------------------------------

/**
 * Serializes and deserializes actor replicated properties.
 *
 * - `serializeActor` produces a JS-value `state` (for next-tick delta comparison) and a
 *   binary `payload` (`Uint8Array | null`) ready to send on the wire.
 * - `deserializeIntoActor` applies a binary payload to an actor and its components.
 *
 * Per-property encoding/decoding is handled by {@link ObjectSerializer}.
 */
export class ReplicationSerializer {
  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Serialize an actor's replicated properties, producing both a JS-value snapshot
   * (for future delta comparison) and a binary wire payload.
   *
   * @param actor           The actor to serialize.
   * @param lastState       The `state` snapshot from the previous call for this actor-client pair,
   *                        or `null` for the initial spawn (produces a full payload).
   * @param isOwningClient  Whether the target client is the actor's owning client.
   *                        Controls filtering of properties marked with `replicateTo: 'owner'`
   *                        or `replicateTo: 'non-owner'`.
   */
  static serializeActor(
    actor: Actor,
    lastState: Record<string, unknown> | null = null,
    isOwningClient: boolean
  ): ActorReplicationSnapshot {
    const writer = new BinaryWriter();
    const actorState: Record<string, unknown> = {};

    const lastActorState = lastState
      ? this.withoutKeys(lastState, [REPLICATION_COMPONENTS_KEY])
      : null;

    const actorChanged = serializeObjectAndEncode(writer, actor, lastActorState, actorState, isOwningClient);

    const lastComponents = lastState
      ? ((lastState[REPLICATION_COMPONENTS_KEY] as Record<string, Record<string, unknown>>) ?? null)
      : null;

    const componentsFull: Record<string, Record<string, unknown>> = {};

    // Reserve the component-count byte; patch it once we know how many components changed.
    const componentCountOffset = writer.offset;
    writer.writeUint8(0);
    let changedComponentCount = 0;

    for (const [subId, component] of actor.getReplicatedComponentMap().entries()) {
      const lastComp = lastComponents?.[component.uuid] ?? null;
      const compState: Record<string, unknown> = {};

      const beforeOffset = writer.offset;
      writer.writeUint8(subId);
      const compChanged = serializeObjectAndEncode(writer, component, lastComp, compState, isOwningClient);

      if (compChanged) {
        changedComponentCount++;
      } else {
        // Rollback sub-ID + bitmask + any values: nothing to send.
        writer.resetTo(beforeOffset);
      }

      componentsFull[component.uuid] = compState;
    }

    writer.patchUint8(componentCountOffset, changedComponentCount);

    const state: Record<string, unknown> = { ...actorState };
    if (Object.keys(componentsFull).length > 0) {
      state[REPLICATION_COMPONENTS_KEY] = componentsFull;
    }

    const hasChanges = actorChanged || changedComponentCount > 0;
    return { state, payload: hasChanges ? writer.getBytes() : null };
  }

  /**
   * Deserialize a binary replication payload into an actor.
   *
   * - Only properties present in the bitmask are touched.
   * - Actor references are resolved via `resolveActorRef`; unresolvable ones are returned
   *   as a Map of `dotPath → uuid` so callers can retry after more actors have spawned.
   * - `onRep*` callbacks fire depth-first (nested → parent).
   *
   * @param actor           Target actor.
   * @param payload         Binary wire payload from `serializeActor`.
   * @param resolveActorRef Resolver for actor UUID → Actor instance.
   * @returns Map of dot-path → referenced actor UUID for any unresolved actor references.
   */
  static deserializeIntoActor(
    actor: Actor,
    payload: Uint8Array,
    resolveActorRef?: (uuid: string) => Actor | null
  ): Map<string, string> {
    const unresolvedRefs = new Map<string, string>(); // dotPath → uuid
    const reader = new BinaryReader(payload);

    decodeObjectFromBinary(reader, actor, resolveActorRef, '', unresolvedRefs);

    const componentCount = reader.readUint8();
    const componentMap = actor.getReplicatedComponentMap();
    for (let i = 0; i < componentCount; i++) {
      const subId = reader.readUint8();
      const component = componentMap[subId];
      if (component) {
        decodeObjectFromBinary(
          reader, component, resolveActorRef,
          `${REPLICATION_COMPONENTS_KEY}.${component.uuid}`, unresolvedRefs
        );
      } else {
        throw new Error(
          `[ReplicationSerializer] Unknown component sub-ID ${subId} in replication payload. ` +
          'Packet is corrupt or component UUIDs have changed.'
        );
      }
    }

    return unresolvedRefs;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private static withoutKeys(
    obj: Record<string, unknown>,
    keys: string[]
  ): Record<string, unknown> {
    const copy = { ...obj };
    for (const key of keys) delete copy[key];
    return copy;
  }
}
